/******************************************************************************

Copyright (c) 2020-2021, Visual Elements AS

******************************************************************************/

// @format

/** Turn a DOM node into an overlay "popup"
 *
 *  @example
 *  //Create an overlay with hello world in it
 *  OverlayModal(cr('h1', '', 'Hello World!'));
 *
 *  @constructor
 *
 *  @emits Show - when the overlay is shown
 *  @emits Hide - when the overlay is hidden
 *
 *  @param {domnode} contents - the DOM node to wrap.
 *  @param {object} attributes - properties for the modal
 *    > width {number} - the width of the modal
 *    > height {number} - the height of the modal
 *    > minWidth {number} - the minimum width of the modal
 *    > minHeight {number} - the minimum height of the modal
 *    > showOnInit {boolean} - if true, the modal will be shown after creation
 *    > zIndex {number} - the Z-Index to use for the modal
 *  @return {object} - A new instance of OverlayModal
 */

import {cr, setStyle, ap, on } from "./highed.dom";
import Events from "./highed.events";
import { merge, isFn, ready } from "./highcharts-editor";
import showDimmer from "./../core/highed.dimmer";

export default class OverlayModal{
  constructor(contents, attributes) {
    this.contents = contents;
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
  
    this.body = cr('div', 'highed-overlay-modal '),
    this.events = Events(),
    this.properties = merge(
      {
        width: 200,
        height: 200,
        minWidth: 10,
        minHeight: 10,
        showOnInit: true,
        zIndex: 10000,
        showCloseIcon: false,
        cancelButton: false
      },
      attributes
    ),
    this.hideDimmer = false,
    this.visible = false;

    if (this.properties.class) {
      this.body.className += this.properties.class;
    }
    ready(() => {
      ap(document.body, this.body);
    });
  
    this.hide(true);
  }

  ///////////////////////////////////////////////////////////////////////////


  /** resize the modal
     *  @memberof OverlayModal
     */

  resize(width, height) {
    this.properties.minWidth = this.properties.width = width;
    this.properties.minHeight = this.properties.height = height;
  }

  resizeModal(){
    setStyle(this.body, {
      width:
      this.properties.width +
        (this.properties.width.toString().indexOf('%') > 0 ? '' : 'px'),
      height:
      this.properties.height +
        (this.properties.height.toString().indexOf('%') > 0 ? '' : 'px'),
      'min-width': this.properties.minWidth + 'px',
      'min-height': this.properties.minHeight + 'px',
    });
  }
  /** Show the modal
     *  @memberof OverlayModal
     */
  show() {
    if (this.visible) return;
    
    setStyle(this.body, {
      width:
      this.properties.width +
        (this.properties.width.toString().indexOf('%') > 0 ? '' : 'px'),
      height:
      this.properties.height +
        (this.properties.height.toString().indexOf('%') > 0 ? '' : 'px'),
      opacity: 1,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      'pointer-events': 'auto',
      'min-width': this.properties.minWidth + 'px',
      'min-height': this.properties.minHeight + 'px',
      'z-index': this.properties.zIndex
    });

    setStyle(document.body, {
      'overflow-x': 'hidden',
      'overflow-y': 'hidden'
    });

    if (this.properties.showCloseIcon) {
      const icon = cr('span', 'highed-overlaymodal-close', '<i class="fa fa-times" aria-hidden="true"></i>');
      on(icon, 'click', () => {
        this.hide();
      });
      ap(this.body, icon);
    }

    this.hideDimmer = showDimmer(
      this.hide,
      true,
      false,
      this.properties.zIndex - 10000
    );

    window.setTimeout(() => {
      this.events.emit('Show');
    }, 300);

    this.visible = true;
    return this.body;
  }

  /** Hide the modal
     *  @memberof OverlayModal
     *  @param suppress {boolean} - suppress the hide event emitting
     */
  hide(suppress) {
    if (!this.visible) return;

    setStyle(this.body, {
      width: '0px',
      height: '0px',
      opacity: 0,
      left: '-20000px',
      'pointer-events': 'none'
    });

    setStyle(document.body, {
      'overflow-x': '',
      'overflow-y': ''
    });

    if (isFn(this.hideDimmer)) {
      this.hideDimmer();
    }

    this.visible = false;

    if (!suppress) {
      this.events.emit('Hide');
    }
  }

  on(type, fn) {
    this.events.on(type, fn);
  }

  getBody(){
    return this.body;
  }
}